import { PERMISSION_TARGET_BATCH_SIZE } from '@/features/authentication/config/constants';
import type {
  AuthorizationOperation,
  AvailableOperationsResponse,
  CapabilityChecker,
  PermissionTarget,
  PermissionTargetRef,
  TargetOperationsResponse,
} from '@/features/authentication/types/permissions';

function permissionTargetKey(target: PermissionTargetRef): string {
  return `${target.type === 'organization' ? 'org' : 'project'}:${target.id}`;
}

function normalizePermissionTargets(targets: readonly PermissionTargetRef[]): PermissionTarget[] {
  const unique = new Map<string, PermissionTarget>();
  for (const target of targets) {
    const key = permissionTargetKey(target);
    unique.set(key, { key, type: target.type, id: target.id });
  }
  return [...unique.values()].sort((left, right) => left.key.localeCompare(right.key));
}

export function batchPermissionTargets(
  targets: readonly PermissionTargetRef[],
): PermissionTarget[][] {
  const normalized = normalizePermissionTargets(targets);
  const batches: PermissionTarget[][] = [];
  for (let index = 0; index < normalized.length; index += PERMISSION_TARGET_BATCH_SIZE) {
    batches.push(normalized.slice(index, index + PERMISSION_TARGET_BATCH_SIZE));
  }
  return batches;
}

export function createCapabilityChecker(
  available: AvailableOperationsResponse | null | undefined,
): CapabilityChecker {
  return (operation) => available?.availableOperations[operation] === true;
}

export function targetAllows(
  targets: TargetOperationsResponse['targets'] | null | undefined,
  target: PermissionTargetRef,
  operation: AuthorizationOperation,
): boolean {
  return targets?.[permissionTargetKey(target)]?.[operation] === true;
}
